import React, { useRef, useState } from 'react';
import { useClickAway } from 'ahooks';
import { not } from 'ramda';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { DownOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Divider } from 'antd';
import { useNavigate } from 'react-router';
import ImprintIconSVG from 'assets/images/icons/paragraph.svg';
import languages from 'utils/languages';
import { getReferralLink } from 'utils/advertisingLinks';
import StyledDropdown from 'Common/Components/Dropdown';
import pkgJson from '../../../../package.json';

const { version: appVersion } = pkgJson;

export type Props = {
  accountName: string;
  handleLogout: () => void;
  switchLanguage: (lang: string) => void;
  currentLanguage: string;
  storeName: string;
  accountMail: string;
};

const AccountInfoWrapper = styled.div`
  padding: 10px 16px;
  > span {
    color: rgba(0, 0, 0, 0.45);
    display: block;
    overflow: hidden;
    white-space: nowrap;
    line-height: 1.7;
    text-overflow: ellipsis;
    font-size: 12px;
    text-transform: uppercase;
  }
`;

const StyledReferralLink = styled.a`
  color: rgba(0, 0, 0, 0.65);
  &:hover {
    color: #0099ff;
  }
`;

const CustomIcon = ({ component: Component, ...rest }: { component: any }) => (
  <Component style={{ fontSize: 16 }} {...rest} />
);

const UserDropdown: React.FC<Props> = ({
  storeName,
  accountMail,
  accountName,
  handleLogout,
  switchLanguage,
  currentLanguage,
}) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const intl = useIntl();

  useClickAway(() => {
    setShowMenu(false);
  }, ref);

  const displayName = storeName || accountName;

  return (
    <div>
      <span ref={ref}>
        <StyledDropdown width="260px">
          <Button
            type="primary"
            shape="circle"
            icon={<UserOutlined />}
            css="margin: 0 25px"
            onClick={() => setShowMenu(not)}
          />
          <div className={`menu ${showMenu ? 'visible' : 'hidden'}`}>
            <AccountInfoWrapper>
              {displayName && <span>{displayName}</span>}
              {accountMail && <span>{accountMail}</span>}
            </AccountInfoWrapper>
            <button
              type="button"
              className={`dropdown-btn ${showLangMenu ? 'active' : 'inactive'}`}
              aria-label="Language selection"
              onClick={() => setShowLangMenu(not)}
            >
              <FormattedMessage defaultMessage="Languages" />
              <DownOutlined />
            </button>

            <div className={`submenu ${showLangMenu ? 'visible' : 'hidden'}`}>
              {languages.map(
                ({ active, prefix, flagIcon, name }) =>
                  active && (
                    <button
                      type="button"
                      key={prefix}
                      disabled={currentLanguage === prefix}
                      className={currentLanguage === prefix ? 'active' : ''}
                      onClick={() => switchLanguage(prefix)}
                      css="padding-top: 5px !important;padding-right: 10px !important"
                    >
                      <FormattedMessage {...name} />
                      <span>{flagIcon}</span>
                    </button>
                  )
              )}
            </div>
            <Divider css="margin: 4px" />
            <button
              type="button"
              aria-label="Log out"
              onClick={() => handleLogout()}
            >
              <FormattedMessage defaultMessage="Log out" />
              <LogoutOutlined />
            </button>
            <button
              type="button"
              aria-label="Imprint"
              onClick={() => navigate('/imprint')}
            >
              <FormattedMessage defaultMessage="Imprint" />
              <CustomIcon component={ImprintIconSVG} />
            </button>
            <small>
              <FormattedMessage
                defaultMessage="App version: {version}"
                values={{ version: appVersion }}
              />
            </small>
            <Divider css="margin: 4px" />
            <Button>
              <StyledReferralLink
                href={getReferralLink(intl.locale.toLowerCase())}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FormattedMessage defaultMessage="Earn up to 500€" />
              </StyledReferralLink>
            </Button>
          </div>
        </StyledDropdown>
      </span>
    </div>
  );
};

export default UserDropdown;
